
// ---- fight-card image import: read a poster (RWS or otherwise) via the
// `sample` capability's vision input, and turn it into {boutNumber,
// redCorner, blueCorner} rows. Matching those rows to existing master cues
// (by boutNumber only — never auto-creating cues) happens in BoardEditor,
// since that's where the board's current master cues live.
const FIGHT_CARD_EXTRACT_PROMPT = `You are reading a Muay Thai (Thai boxing) fight-card poster or graphic — most often from Rajadamnern World Series (RWS), but it could be from another promoter. It lists a schedule of bouts, each usually labeled with a bout number in Thai as "คู่ที่ N" (or similar, e.g. "BOUT N" / "Fight N"), plus the two boxers fighting in it.

For every bout shown on the poster — both preliminary bouts and main-card bouts — extract:
- "boutNumber": the bout number, as a string of digits only (e.g. "1", "2"). If a bout has no number printed anywhere on it, omit that bout entirely from the results.
- "redCorner": the name of the boxer in the red corner, exactly as printed — do not translate or transliterate it. Posters commonly place the red-corner boxer on the left, or mark a corner with red styling/text; use whichever cue is visible. If you truly cannot tell which side is red vs blue, put the first-listed/left-hand boxer here.
- "blueCorner": the name of the boxer in the blue corner, exactly as printed, using the same reasoning (right side / blue styling, or the second-listed boxer when it can't be told apart).

Reply with ONLY a JSON array of objects shaped like {"boutNumber": string, "redCorner": string, "blueCorner": string} — one per bout, in the order they appear on the poster. No other text, no markdown fence.`;

function normalizeFightCardRows(data) {
  if (!Array.isArray(data)) return [];
  const rows = [];
  for (const item of data) {
    if (!item || typeof item !== "object") continue;
    const boutNumber = String(item.boutNumber ?? "").trim().replace(/[^\d]/g, "");
    const redCorner = String(item.redCorner ?? "").trim();
    const blueCorner = String(item.blueCorner ?? "").trim();
    if (!boutNumber) continue;
    rows.push({ boutNumber, redCorner, blueCorner });
  }
  return rows;
}
// Groups SampleErrorCode by the copy this UI shows — see the `sample`
// capability's own error-code doc for the full reasoning per code.
function fightCardErrorMessage(e, t) {
  const code = e && e.code;
  if (code === "rate_limited") return t("fightCardErrRateLimited");
  if (code === "image_rejected") return t("fightCardErrImageRejected");
  if (code === "content_rejected" || code === "refused" || code === "empty_completion" || code === "invalid_json" || code === "upstream_error") return t("fightCardErrRefused");
  if (
    code === "not_granted" || code === "sampling_disabled" || code === "not_declared" ||
    code === "images_unavailable" || code === "capability_disabled" || code === "capability_removed"
  ) return t("fightCardErrUnavailable");
  return t("fightCardErrGeneric");
}
// Same idea as fightCardErrorMessage() above, but for the "paste a link" mode
// (self-hosted deploy targets only — see Cap.sample.fromUrl in src/platform/api.js)
// — separate wording for the link-specific failure codes a URL fetch can hit
// (invalid_url, fetch_failed, unsupported_content, content_too_large) that
// don't make sense phrased as being about a photo.
function fightCardUrlErrorMessage(e, t) {
  const code = e && e.code;
  if (code === "rate_limited") return t("fightCardErrRateLimited");
  if (code === "invalid_url") return t("fightCardErrInvalidUrl");
  if (code === "fetch_failed" || code === "unsupported_content" || code === "content_too_large") return t("fightCardErrUrlFetchFailed");
  if (code === "content_rejected" || code === "empty_completion" || code === "invalid_json" || code === "upstream_error") return t("fightCardErrUrlRefused");
  if (
    code === "not_granted" || code === "sampling_disabled" || code === "not_declared" ||
    code === "images_unavailable" || code === "capability_disabled" || code === "capability_removed"
  ) return t("fightCardErrUnavailable");
  return t("fightCardErrGeneric");
}
// codes that mean this view can never run this feature again this load —
// once hit, stop showing the file picker/link input/scan button and just
// show the message. Shared by both import modes since they're all about the
// AI capability as a whole being unavailable, not about one input mode.
const FIGHT_CARD_HIDE_FOREVER_CODES = new Set([
  "not_granted", "sampling_disabled", "not_declared",
  "images_unavailable", "capability_disabled", "capability_removed",
]);

export { FIGHT_CARD_EXTRACT_PROMPT, normalizeFightCardRows, fightCardErrorMessage, fightCardUrlErrorMessage, FIGHT_CARD_HIDE_FOREVER_CODES };
